import { Sidebar } from "primereact/sidebar";
import { Button } from "primereact/button";
import { useState, useEffect } from "react";
import { Ripple } from "primereact/ripple";
import { useNavigate, useLocation } from "react-router-dom";

const sidebarStyles = `
  /* ── Tooltip blanco ── */
  .p-tooltip .p-tooltip-text {
    background: #ffffff !important;
    color: #1a1d23 !important;
    border: 1px solid #e8eaed !important;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
    border-radius: 8px !important;
    font-size: 0.78rem !important;
    font-weight: 600 !important;
    padding: 6px 12px !important;
  }
  .p-tooltip .p-tooltip-arrow {
    border-right-color: #e8eaed !important;
  }

  /* ── Strip vertical desktop ── */
  .sc-floating {
    position: fixed;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    z-index: 200;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    background: #ffffff;
    border: 1px solid #e8eaed;
    border-left: none;
    border-radius: 0 16px 16px 0;
    padding: 12px 8px;
    box-shadow: 4px 0 20px rgba(0,0,0,0.08);
  }

  .sc-floating .sc-nav-btn.p-button {
    width: 40px !important;
    height: 40px !important;
    border-radius: 10px !important;
    background: transparent !important;
    border: none !important;
    color: #6b7280 !important;
    box-shadow: none !important;
    padding: 0 !important;
    transition: background 0.15s, color 0.15s !important;
  }
  .sc-floating .sc-nav-btn.p-button:hover {
    background: #e6faf2 !important;
    color: #00A86B !important;
  }
  .sc-floating .sc-nav-btn.p-button .p-button-icon {
    font-size: 1rem;
  }

  /* Botón activo en desktop */
  .sc-floating .sc-nav-btn.sc-active.p-button {
    background: #e6faf2 !important;
    color: #00A86B !important;
  }

  .sc-floating .sc-menu-btn.p-button {
    width: 40px !important;
    height: 40px !important;
    border-radius: 10px !important;
    background: #00A86B !important;
    border: none !important;
    color: #fff !important;
    box-shadow: 0 2px 10px rgba(0,168,107,0.3) !important;
    padding: 0 !important;
    margin-bottom: 4px;
  }
  .sc-floating .sc-menu-btn.p-button:hover {
    background: #009960 !important;
  }

  .sc-divider {
    width: 24px;
    height: 1px;
    background: #e8eaed;
    margin: 2px 0;
  }

  /* ── Mobile: bottom bar ── */
  @media (max-width: 768px) {
    .sc-floating {
      left: 0;
      right: 0;
      top: auto;
      bottom: 0;
      transform: none;
      width: 100%;
      flex-direction: row;
      justify-content: space-around;
      border-radius: 16px 16px 0 0;
      border: none;
      border-top: 1px solid #e8eaed;
      padding: 8px 16px 10px;
      box-shadow: 0 -4px 20px rgba(0,0,0,0.08);
    }
    .sc-divider       { display: none; }
    .sc-menu-btn-wrap { display: none; }

    .hr-main { padding-bottom: 90px !important; }

    /* En mobile cada botón tiene posición relativa para el dot */
    .sc-floating .sc-nav-btn.p-button {
      position: relative !important;
    }

    /* Señita verde debajo del icono activo en mobile */
    .sc-floating .sc-nav-btn.sc-active.p-button::after {
      content: '';
      position: absolute;
      bottom: 2px;
      left: 50%;
      transform: translateX(-50%);
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #00A86B;
    }

    /* Icono activo en mobile */
    .sc-floating .sc-nav-btn.sc-active.p-button {
      background: transparent !important;
      color: #00A86B !important;
    }

    .p-tooltip { display: none !important; }
  }
`;

const NAV_ITEMS = [
  { path: "/inicio",   icon: "pi-home",      label: "Inicio"          },
  { path: "/profile",  icon: "pi-user",      label: "Perfil"          },
  { path: "/transfer", icon: "pi-chart-bar", label: "Transacciones"   },
  { path: "/support",  icon: "pi-wrench",    label: "Soporte"         },
  { path: "/",         icon: "pi-sign-out",  label: "Salida segura", isDivider: true },
];

function SidebarComponent(): JSX.Element {
  const [visible,  setVisible]  = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);
  const navigate  = useNavigate();
  const location  = useLocation();

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const isActive = (path: string) =>
    path === "/" ? false : location.pathname === path;

  return (
    <>
      <style>{sidebarStyles}</style>

      {/* ── Drawer ── */}
      <Sidebar
        visible={visible}
        onHide={() => setVisible(false)}
        content={({ hide }): JSX.Element => (
          <div
            id="app-sidebar-2"
            className="surface-section h-screen block flex-shrink-0 absolute lg:static left-0 top-0 z-1 border-right-1 surface-border select-none"
            style={{ width: "280px" }}
          >
            <div className="flex flex-column h-full">
              <div className="flex align-items-center justify-content-between px-4 pt-3 flex-shrink-0">
                <span className="inline-flex align-items-center gap-2">
                  <span className="font-semibold text-2xl text-primary">Menú</span>
                </span>
                <Button
                  type="button"
                  onClick={(e) => hide(e)}
                  icon="pi pi-times"
                  rounded
                  outlined
                  className="h-2rem w-2rem"
                />
              </div>
              <div className="overflow-y-auto">
                <ul className="list-none p-3 m-0">
                  {NAV_ITEMS.map(({ path, icon, label }) => (
                    <li key={path}>
                      <a
                        href={path}
                        className="p-ripple flex align-items-center cursor-pointer p-3 border-round text-700 hover:surface-100 transition-duration-150 transition-colors w-full"
                      >
                        <i className={`pi ${icon} mr-2`} />
                        <span className="font-medium">{label}</span>
                        <Ripple />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      />

      {/* ── Floating nav ── */}
      <nav className="sc-floating">

        {/* Hamburguesa — solo desktop */}
        <span className="sc-menu-btn-wrap">
          <Button
            className="sc-menu-btn"
            icon="pi pi-bars"
            tooltip={!isMobile ? "Menú" : undefined}
            tooltipOptions={{ position: "right" }}
            onClick={() => setVisible(true)}
          />
        </span>

        <div className="sc-divider" />

        {NAV_ITEMS.map(({ path, icon, label, isDivider }) => (
          <span key={path}>
            {isDivider && <div className="sc-divider" />}
            <Button
              className={`sc-nav-btn${isActive(path) ? " sc-active" : ""}`}
              icon={`pi ${icon}`}
              tooltip={!isMobile ? label : undefined}
              tooltipOptions={{ position: "right" }}
              onClick={() => navigate(path)}
            />
          </span>
        ))}

      </nav>
    </>
  );
}

export default SidebarComponent;